import { OpenTabDetailFragment, OpenTabState } from '@/features/graphql/generated/graphql'
import { AccountHistoryEvent } from '@/features/openTabs/types'
import {
    getLatestSettlement,
    getProductVariantName,
    resolveCurrency,
    toAmount,
} from '@/features/openTabs/utils/backendValues'

const eventTypeOrder: Record<AccountHistoryEvent['type'], number> = {
    created: 0,
    'order-added': 1,
    payment: 2,
    closed: 3,
}

const toHistoryItemLines = (
    item: OpenTabDetailFragment['submissions'][number]['items'][number],
    quantity: number
): string[] => [
    `${quantity}x ${getProductVariantName(item.productVariant)}`,
    ...item.addedProductVariants.map(
        (addition) =>
            `${addition.quantity * quantity}x ${getProductVariantName(addition.productVariant)}`
    ),
    ...item.toppings.map((topping) => `${topping.quantity * quantity}x ${topping.topping.name}`),
    ...item.removedRecipeIngredients.map((ingredient) => `- ${ingredient.name}`),
]

export const toAccountHistory = (
    tab: OpenTabDetailFragment,
    fallbackCurrency: string
): AccountHistoryEvent[] => {
    const events: AccountHistoryEvent[] = [
        {
            id: `${tab.uuid}-created`,
            type: 'created',
            occurredAt: String(tab.createdAt),
        },
        ...tab.submissions.map((submission) => ({
            id: `${submission.uuid}-added`,
            type: 'order-added' as const,
            occurredAt: String(submission.createdAt),
            amount: toAmount(submission.totalPrice.amount),
            currency: resolveCurrency(fallbackCurrency, submission.totalPrice),
            itemCount: submission.items.reduce((count, item) => count + item.quantity, 0),
            itemNames: submission.items.flatMap((item) => toHistoryItemLines(item, item.quantity)),
        })),
        ...tab.settlements.map((settlement) => ({
            id: `${settlement.uuid}-payment`,
            type: 'payment' as const,
            occurredAt: String(settlement.createdAt),
            amount: toAmount(settlement.order.totalPrice.amount),
            currency: resolveCurrency(fallbackCurrency, settlement.order.totalPrice),
            paymentMethod: settlement.order.paymentType,
            itemNames: settlement.items.flatMap((item) =>
                toHistoryItemLines(item.openTabItem, item.quantity)
            ),
        })),
    ]

    const lastSettlement = getLatestSettlement(tab)
    if (tab.state === OpenTabState.Closed && lastSettlement) {
        const occurredAt = String(lastSettlement.createdAt)

        events.push({
            id: `${tab.uuid}-closed`,
            type: 'closed',
            occurredAt,
        })
    }

    return events.sort(
        (left, right) =>
            Date.parse(left.occurredAt) - Date.parse(right.occurredAt) ||
            eventTypeOrder[left.type] - eventTypeOrder[right.type] ||
            left.id.localeCompare(right.id)
    )
}
